import { useQuery } from "@tanstack/react-query";

export interface EffectivePermissionsResponse {
  userId: string;
  role: string;
  workspaceId: string;
  isViewingAs: boolean;
  actualUserId?: string;
  actualRole?: string;
  permissions: Record<string, boolean>;
}

interface UseEffectivePermissionsOptions {
  viewAsUserId?: string | null;
  enabled?: boolean;
}

const FETCH_TIMEOUT_MS = 12_000;

export function useEffectivePermissions(options?: UseEffectivePermissionsOptions) {
  const viewAsUserId = options?.viewAsUserId;
  const enabled = options?.enabled !== false;

  const query = useQuery<EffectivePermissionsResponse>({
    queryKey: ["/api/permissions/effective", viewAsUserId],
    enabled,
    queryFn: async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const headers: Record<string, string> = {};
        if (viewAsUserId) {
          headers["X-View-As-UserId"] = viewAsUserId;
        }
        const response = await fetch("/api/permissions/effective", {
          credentials: "include",
          headers,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("Failed to fetch permissions");
        }
        return response.json();
      } finally {
        clearTimeout(timer);
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Be resilient to transient backend hiccups (cold DB starts, brief 5xx).
    // Without this, a single failed fetch leaves `permissions` empty and
    // every route renders "Access Denied" even for admins.
    retry: 4,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  const can = (permissionKey: string): boolean => {
    return query.data?.permissions?.[permissionKey] === true;
  };

  const hasAny = (...permissionKeys: string[]): boolean => {
    if (!query.data?.permissions) return false;
    return permissionKeys.some((key) => query.data.permissions[key] === true);
  };

  const hasAll = (...permissionKeys: string[]): boolean => {
    if (!query.data?.permissions) return false;
    return permissionKeys.every((key) => query.data.permissions[key] === true);
  };

  return {
    ...query,
    can,
    hasAny,
    hasAll,
    permissions: query.data?.permissions || {},
    role: query.data?.role,
    userId: query.data?.userId,
    isViewingAs: query.data?.isViewingAs || false,
    actualUserId: query.data?.actualUserId,
    actualRole: query.data?.actualRole,
  };
}
